import { Badge } from "@admin/components/atoms/Badge";
import { Button } from "@admin/components/atoms/Button";
import useFoodManagementModals from "@admin/hooks/useFoodManagementModals";
import {
  categorizeFoodControlPoints,
  ControlPointCategory,
  FoodControlPointInstanceDto,
  isDisplayNameTypes,
  useControlPointTypeTranslations,
} from "@enos-monorepo/lib";
import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ControlPointModals } from ".";

interface WaitingControlsProps {
  controls: FoodControlPointInstanceDto[];
}

export function WaitingControls({ controls }: WaitingControlsProps) {
  const { t, i18n } = useTranslation();
  const controlPointTypeTranslations = useControlPointTypeTranslations();
  const groupedControls = useMemo(() => {
    const points = categorizeFoodControlPoints(controls);
    points["Inventory Controls"]?.sort((a, b) =>
      (a.controlPoint?.name ?? "").localeCompare(b.controlPoint?.name ?? ""),
    );
    return points;
  }, [controls]);
  const [currentInstance, setCurrentInstance] =
    useState<FoodControlPointInstanceDto>();
  const { openModal, closeModals, modalOpen } = useFoodManagementModals();

  return (
    <div className="card card-grid min-w-full">
      <div className="card-header py-5 flex-wrap justify-between">
        <div className="flex flex-row items-center gap-10">
          <h3 className="card-title">{t("food.Waiting Controls")}</h3>
        </div>
        <div className="flex flex-row gap-5"></div>
      </div>

      {/* Main table starts here */}
      <table className="table table-border table-fixed align-middle w-full">
        <tbody>
          {/* Render each category of control types */}
          {Object.keys(groupedControls)
            .filter((type) => type !== "Unknown Category")
            .map((type) => {
              const controlsOfType =
                groupedControls[type as ControlPointCategory];

              return (
                <Fragment key={type}>
                  <tr className="bg-[#FCFCFC]">
                    <th className="text-center w-[150px]">{t("food.State")}</th>
                    <th className="text-center">
                      {t(`food.${type as ControlPointCategory}`)}
                    </th>
                    <th className="text-center">{t("food.Due Date")}</th>
                    <th className="text-center w-[120px]" />
                  </tr>

                  {/* If there are no controls, display "No data" */}
                  {controlsOfType.length === 0 &&
                  type !== "Unknown Category" ? (
                    <tr>
                      <td colSpan={4} className="text-center text-gray-500">
                        {t("common.No Data")}
                      </td>
                    </tr>
                  ) : (
                    // Render controls within the category
                    controlsOfType.map((instance) => (
                      <tr key={instance.id}>
                        <td className="text-center">
                          <div className="flex flex-row justify-center">
                            <Badge type="warning">
                              {t(
                                `food.${
                                  (instance.status.charAt(0).toUpperCase() +
                                    instance.status.slice(
                                      1,
                                    )) as ControlPointCategory
                                }`,
                              )}
                            </Badge>
                          </div>
                        </td>
                        <td>
                          {instance.controlPoint?.type &&
                            (!isDisplayNameTypes(instance.controlPoint?.type)
                              ? controlPointTypeTranslations[
                                  instance.controlPoint?.type
                                ]
                              : instance.controlPoint?.name)}
                        </td>
                        <td className="text-center">
                          {Intl.DateTimeFormat(i18n.language, {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          }).format(new Date(instance.endDate))}
                        </td>
                        <td className="text-center w-[120px]">
                          <Button
                            size="sm"
                            onClick={() => {
                              setCurrentInstance(instance);
                              if (instance.controlPoint)
                                openModal(instance.controlPoint?.type);
                            }}
                          >
                            <i className="ki-filled ki-plus-squared"></i>
                            {t("food.Entry")}
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </Fragment>
              );
            })}
        </tbody>
      </table>
      <ControlPointModals
        currentInstance={currentInstance}
        closeModals={closeModals}
        modalOpen={modalOpen}
      />
    </div>
  );
}
